/**
 * Config-driven helpers for approval-workflow UI on SKU rows.
 *
 * Pass raw eligibility ui_rules into resolveSkuStatusLock with current
 * attribute values. First matching config wins (lock + its value together).
 */

import type { ApprovalConfigurationUIRules } from "../../../../../../types/types.Workflow";

export type AttributeColumnLike = {
  id: string;
  object_type?: string | null;
  new_object_type?: string | null;
  name?: string | null;
  display_name?: string | null;
};

function norm(value: string | null | undefined): string {
  return (value ?? "").toLowerCase().trim();
}

/** Read a name/value string from a chip, select option, or plain string. */
export function getCellName(cell: unknown): string {
  if (cell == null) return "";
  if (typeof cell === "string") return cell;
  if (Array.isArray(cell)) return cell.length ? getCellName(cell[0]) : "";
  if (typeof cell === "object") {
    const obj = cell as {
      name?: string;
      value?: string;
      label?: string;
    };
    return obj.name ?? obj.value ?? obj.label ?? "";
  }
  return String(cell);
}

function matchSatisfied(
  attributeValues: Record<string, string>,
  match: Record<string, unknown>
): boolean {
  const entries = Object.entries(match ?? {}).filter(([key, value]) => {
    const expected =
      typeof value === "string" ? value.trim() : String(value ?? "").trim();
    return !!key.trim() && !!expected;
  });
  if (!entries.length) return false;
  return entries.every(([key, rawExpected]) => {
    const expected =
      typeof rawExpected === "string"
        ? rawExpected.trim()
        : String(rawExpected ?? "").trim();
    const actual = attributeValues[norm(key)];
    if (expected === "*") {
      return !!actual;
    }
    return !!actual && norm(actual) === norm(expected);
  });
}

/**
 * Resolve SKU Status lock from raw configuration ui_rules.
 * Returns the value from the same config whose match succeeded.
 */
export function resolveSkuStatusLock(args: {
  collectionName?: string | null;
  attributeValues: Record<string, string>;
  uiRules?: ApprovalConfigurationUIRules[] | null;
}): { locked: boolean; value?: string } {
  if (!(norm(args.collectionName) === "skus")) {
    return { locked: false };
  }

  for (const config of args.uiRules ?? []) {
    const lockRule = config.rules?.lock_sku_status;
    if (!lockRule?.matches?.length) continue;

    const matched = lockRule.matches.some((match) =>
      matchSatisfied(args.attributeValues, match)
    );
    if (!matched) continue;

    const value = lockRule.value?.trim() || undefined;
    return { locked: true, value };
  }

  return { locked: false };
}

/**
 * Build a lookup of attribute values keyed by object_type / name / display_name.
 * Form values (getFormValue) override row values while editing.
 */
export function buildSkuAttributeValueLookup(args: {
  columns?: AttributeColumnLike[];
  row?: Record<string, unknown>;
  getFormValue?: (columnId: string) => unknown;
}): Record<string, string> {
  const { columns = [], row = {}, getFormValue } = args;
  const values: Record<string, string> = {};

  const setAliases = (
    aliases: Array<string | null | undefined>,
    value: string
  ) => {
    if (!value) return;
    for (const alias of aliases) {
      const key = norm(alias);
      if (key) values[key] = value;
    }
  };

  for (const col of columns) {
    const formRaw = getFormValue?.(col.id);
    const raw = formRaw != null && formRaw !== "" ? formRaw : row[col.id];
    const value = getCellName(raw);
    setAliases(
      [
        col.object_type,
        col.new_object_type,
        col.name,
        col.display_name,
        col.id,
      ],
      value
    );
  }

  // Region is often the sku_source chip rather than a named "region" column.
  if (!values.region) {
    const skuSourceEntry = Object.entries(row).find(([, v]) => {
      if (Array.isArray(v) && v.length > 0) {
        return Boolean((v[0] as { is_sku_source?: boolean })?.is_sku_source);
      }
      return Boolean((v as { is_sku_source?: boolean } | null)?.is_sku_source);
    });
    if (skuSourceEntry) {
      setAliases(["region"], getCellName(skuSourceEntry[1]));
    }
  }

  return values;
}
